import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    UerExcludeContractObjectFromWorkOrderChangeMutation,
    UerIncludeContractObjectToWorkOrderChangeMutation,
} from 'gql/graphql';
import type {
    IUseIncludeExcludeWorkOrderObjectsDataProps,
    IUseIncludeExcludeWorkOrderObjectsDataResult,
} from 'pages/Project/Common/WorkOrderStructure/types';
import { WorkOrderStructureOperationType } from 'pages/Project/Common/WorkOrderStructure/types';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    uerExcludeContractObjectFromWorkOrderChangeGql,
    uerIncludeContractObjectToWorkOrderChangeGql,
} from '../../WorkOrderChangeQueries';

const includeRequestId = 'uerIncludeContractObjectToWorkOrderChange';
const excludeRequestId = 'uerExcludeContractObjectFromWorkOrderChange';

export const useIncludeExcludeUerWorkOrderChangeObjectsData = (
    props: IUseIncludeExcludeWorkOrderObjectsDataProps,
): IUseIncludeExcludeWorkOrderObjectsDataResult => {
    const dispatch = useDispatch();

    const { workOrderOrChangeId, onSuccess } = props;

    const [uerIncludeContractObjectToWorkOrderChangeReset] = useResetStoreData({ requestId: includeRequestId });
    const [uerExcludeContractObjectFromWorkOrderChangeReset] = useResetStoreData({ requestId: excludeRequestId });

    const onIncludeSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerIncludeContractObjectToWorkOrderChangeMutation>) => {
            onSuccess(
                WorkOrderStructureOperationType.Include,
                depsVars.payload.uerIncludeContractObjectToWorkOrderChange.data || [],
            );

            uerIncludeContractObjectToWorkOrderChangeReset(undefined);
        },
        [onSuccess, uerIncludeContractObjectToWorkOrderChangeReset],
    );

    const [, uerIncludeContractObjectToWorkOrderChangeStatus, , uerIncludeContractObjectToWorkOrderChangeRequest] =
        useRequest({
            requestId: includeRequestId,
            query: uerIncludeContractObjectToWorkOrderChangeGql,
            withEffect: false,
            dependenciesUpdater: onIncludeSuccess,
        });

    const onExcludeSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerExcludeContractObjectFromWorkOrderChangeMutation>) => {
            onSuccess(
                WorkOrderStructureOperationType.Exclude,
                depsVars.payload.uerExcludeContractObjectFromWorkOrderChange?.data || [],
            );

            uerExcludeContractObjectFromWorkOrderChangeReset(undefined);
        },
        [onSuccess, uerExcludeContractObjectFromWorkOrderChangeReset],
    );

    const [, uerExcludeContractObjectFromWorkOrderChangeStatus, , uerExcludeContractObjectFromWorkOrderChangeRequest] =
        useRequest({
            requestId: excludeRequestId,
            query: uerExcludeContractObjectFromWorkOrderChangeGql,
            withEffect: false,
            dependenciesUpdater: onExcludeSuccess,
        });

    useEffect(() => {
        uerExcludeContractObjectFromWorkOrderChangeReset(undefined);
        uerIncludeContractObjectToWorkOrderChangeReset(undefined);
    }, [uerExcludeContractObjectFromWorkOrderChangeReset, uerIncludeContractObjectToWorkOrderChangeReset]);

    const makeRequest = useCallback(
        (operation: WorkOrderStructureOperationType, contractObjectId: number | undefined) => {
            uerExcludeContractObjectFromWorkOrderChangeReset(undefined);
            uerIncludeContractObjectToWorkOrderChangeReset(undefined);

            if (operation === WorkOrderStructureOperationType.Include) {
                dispatch(
                    uerIncludeContractObjectToWorkOrderChangeRequest({
                        workOrderChangeId: workOrderOrChangeId,
                        contractObjectId,
                    }),
                );
            }

            if (operation === WorkOrderStructureOperationType.Exclude) {
                dispatch(
                    uerExcludeContractObjectFromWorkOrderChangeRequest({
                        workOrderChangeId: workOrderOrChangeId,
                        contractObjectId,
                    }),
                );
            }
        },
        [
            dispatch,
            uerExcludeContractObjectFromWorkOrderChangeRequest,
            uerExcludeContractObjectFromWorkOrderChangeReset,
            uerIncludeContractObjectToWorkOrderChangeRequest,
            uerIncludeContractObjectToWorkOrderChangeReset,
            workOrderOrChangeId,
        ],
    );

    const status = useMemo(
        () => uerIncludeContractObjectToWorkOrderChangeStatus || uerExcludeContractObjectFromWorkOrderChangeStatus,
        [uerExcludeContractObjectFromWorkOrderChangeStatus, uerIncludeContractObjectToWorkOrderChangeStatus],
    );

    return {
        makeRequest,
        status,
    };
};
